import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Users, Home } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/Card";
import { useOnboardingStore } from "@/stores/onboardingStore";
import { useAuth } from "@/contexts/AuthContext";
import { onboardingApi, ApiError } from "@/lib/api";

// Step 2 — domain exists. One CTA: notify the admin. After the request lands
// we show a terminal confirmation card. We never reveal the existing owner's
// email or client id here — backend includes `existing_client_id` in the
// verify response but the UI intentionally does not render it.
export function StepTeammateRequested() {
  const { idToken } = useAuth();
  const onboardingId = useOnboardingStore((s) => s.onboardingId);
  const domain = useOnboardingStore((s) => s.domain);
  const [submitting, setSubmitting] = useState(false);
  const [notified, setNotified] = useState(false);

  const notify = async () => {
    if (!onboardingId) {
      toast.error("Please restart onboarding");
      return;
    }
    if (!idToken) {
      toast.error("Please sign in again");
      return;
    }
    setSubmitting(true);
    try {
      await onboardingApi.requestTeammateAccess(onboardingId, idToken);
      setNotified(true);
      toast.success("We've notified the admin");
    } catch (err) {
      const ae = err as ApiError;
      console.error("requestTeammateAccess", ae);
      toast.error("Couldn't notify the admin", { description: ae.message });
    } finally {
      setSubmitting(false);
    }
  };

  if (notified) {
    return (
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="rounded-md bg-signal-ok/15 p-2 text-signal-ok">
              <CheckCircle2 className="h-5 w-5" />
            </div>
            <div>
              <CardTitle>Request sent</CardTitle>
              <CardDescription>
                We've notified the admin at{" "}
                <span className="font-mono text-foreground">{domain ?? "your company"}</span>.
                Please follow up with them to invite you.
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Button size="lg" asChild>
            <a href="/">
              <Home className="h-4 w-4" />
              Go to homepage
            </a>
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="rounded-md bg-brand-violet/15 p-2 text-brand-violet">
            <Users className="h-5 w-5" />
          </div>
          <div>
            <CardTitle>You're not the first</CardTitle>
            <CardDescription>
              Someone at{" "}
              <span className="font-mono text-foreground">{domain ?? "your company"}</span>{" "}
              has already set up VirtualDesk.
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <Button size="lg" onClick={notify} disabled={submitting}>
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          Notify the admin to invite me
        </Button>
      </CardContent>
    </Card>
  );
}
